import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  OnInit,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { Sort } from '@angular/material/sort';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, timer } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DateTime as LuxonDateTime } from 'luxon';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { InquiriesService } from 'src/shared/services/inquiries.service';
import { Client } from 'src/providers/client/client';

import { ReservationPage } from 'src/pages/reservation/reservation';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import { Util } from 'src/shared/util/util';

import { Inquiry } from 'src/models/inquiry.model';
import {
  InquiryLifecycle,
  InquiryStatusTone,
  inquiryStatusLabel,
  inquiryStatusPresentation,
} from 'src/shared/util/inquiry-status';

// Pre-computed display model so the template only binds strings (no template
// function calls, per the codebase conventions).
interface InquiryVM {
  id: number;
  addressId: number;
  propertyName: string;
  source: string; // raw channel key — picks the logo and its brand colour
  sourceName: string;
  sourceLogo: string | null;
  guestName: string;
  propertyLine: string;
  dateRange: string;
  checkInIso: string; // raw check-in date ('' when unknown), for table sorting
  guestCount: number;
  totalCents: number;
  guestsLabel: string | null;
  totalLabel: string;
  message: string | null;
  status: string; // presented lifecycle status — what the status filter matches
  statusLabel: string;
  statusDetail: string | null;
  statusTone: InquiryStatusTone;
  statusOrder: number; // lifecycle rank, for sorting the table's Status column
  // The status fields above are a snapshot of a moving target — a pre-approval
  // window lapses with no new data — so the row keeps what they were derived
  // from and the minute tick recomputes them. See refreshStatusBadges().
  lifecycle: InquiryLifecycle;
  searchText: string; // lowercased blob used for the search filter
}

interface PropertyFilterItem {
  value: number;
  viewValue: string;
}

interface StatusFilterItem {
  value: string;
  viewValue: string;
}

// Same assets and brand colours the Listings page puts beside a connected
// listing, so one channel looks identical wherever the app names it. The
// colours live in the stylesheet, keyed off the source; this is just the mark.
// 'other' is deliberately absent — an unknown channel gets its name, no badge.
const SOURCE_LOGOS: Record<string, string> = {
  airbnb: 'assets/img/airbnb-belo.svg',
  vrbo: 'assets/img/vrbo-icon.png',
  booking_com: 'assets/img/booking-com-icon.png',
  direct_booking: 'assets/img/bowtie-white.svg',
  tidy: 'assets/img/bowtie-white.svg',
};

// The lifecycle states a host can filter down to. There is deliberately no
// "All" entry: an empty filter IS all, and the chip's absence says so more
// clearly than a chip reading "All" ever could.
//
// 'responded' ("Replied") is written by the backend once a host answers an
// inquiry on the channel itself (Trello 18656) — until that ships, the chip
// simply filters to an empty list. It is NOT in the InquiryStatus union on
// purpose: the union mirrors the backend's current STATUS_VALUES, and the
// status utils already take `InquiryStatus | string` for exactly this
// ahead-of-the-backend window.
const STATUS_FILTERS: readonly string[] = [
  'pending',
  'preapproved',
  'approved',
  'responded',
  'declined',
  'expired',
];

@Component({
  selector: 'app-inquiries-page',
  templateUrl: './inquiries.page.html',
  styleUrls: ['./inquiries.page.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    ReactiveFormsModule,
    TidyUiComponentsModule,
    SharedModule,
    MatMenuModule,
    MatCheckboxModule,
    MatIconModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class InquiriesPage implements OnInit {
  readonly windowService = inject(WindowService);
  private readonly service = inject(InquiriesService);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly client = inject(Client);

  // The inquiries payload's address hash carries only address1/address2/city —
  // never the host's nickname for the property. The cached address list does,
  // so rows are labelled from it (by address_id) and fall back to the payload.
  private addressesById = new Map<number, any>();

  private static readonly ALL_PROPERTIES = 0;

  readonly isLoading = signal<boolean>(true);
  readonly skeletonCards = [0, 1, 2];

  // Shared tidy-table (same component as Listings & co.) column set. Every
  // control on a row now lives on the inquiry detail page, so the trailing
  // 'last-column' carries only the chevron that opens it — the component
  // renders that automatically for a column of this name.
  readonly tableHeaders = ['Guest', 'Property', 'Source', 'Dates', 'Guests', 'Special Request', 'Total', 'Status', 'last-column'];
  private readonly sortState = signal<Sort | null>(null);

  // Full set from the service; the displayed list is derived via search/filter.
  private readonly allInquiries = signal<InquiryVM[]>([]);
  private readonly searchTerm = signal<string>('');
  private readonly selectedPropertyId = signal<number>(InquiriesPage.ALL_PROPERTIES);

  // Lands on the rows that still need a response, because that is the only
  // reason to open this page unprompted. Clearing the chip widens to all.
  readonly selectedStatus = signal<string | null>('pending');

  readonly statusFilterItems: StatusFilterItem[] = STATUS_FILTERS.map((value) => ({
    value,
    viewValue: inquiryStatusLabel(value),
  }));

  readonly statusChipLabel = computed<string | null>(() => {
    const status = this.selectedStatus();
    return status ? inquiryStatusLabel(status) : null;
  });

  readonly searchForm = this.fb.group({
    search: [''],
    selectedProperty: [InquiriesPage.ALL_PROPERTIES],
  });

  readonly distinctProperties = computed<PropertyFilterItem[]>(() => {
    const seen = new Map<number, string>();
    for (const i of this.allInquiries()) {
      if (!seen.has(i.addressId)) {
        seen.set(i.addressId, i.propertyName);
      }
    }
    return Array.from(seen, ([value, viewValue]) => ({ value, viewValue }));
  });

  readonly propertyFilterItems = computed<PropertyFilterItem[]>(() => [
    { value: InquiriesPage.ALL_PROPERTIES, viewValue: 'All Properties' },
    ...this.distinctProperties(),
  ]);

  readonly hasAnyInquiries = computed<boolean>(() => this.allInquiries().length > 0);

  // Displayed inquiries after applying the search term, property and status.
  readonly inquiries = computed<InquiryVM[]>(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const propertyId = this.selectedPropertyId();
    const status = this.selectedStatus();
    return this.allInquiries().filter((i) => {
      const matchesProperty =
        propertyId === InquiriesPage.ALL_PROPERTIES || i.addressId === propertyId;
      const matchesSearch = !term || i.searchText.includes(term);
      const matchesStatus = !status || i.status === status;
      return matchesProperty && matchesSearch && matchesStatus;
    });
  });

  // The badge counts threads the guest is still waiting on AND the host can
  // still act on. An answered question is not an action item; neither is a row
  // that can no longer be acted on — that one badges as Expired, so counting it
  // would put the header counter at odds with the badge beside it. It reads the
  // filtered list, not the full one: a host filtering by property to work
  // through them needs a number that matches what is on screen.
  readonly awaitingCount = computed<number>(
    () => this.inquiries().filter((i) => i.status === 'pending').length
  );

  // tidy-table rows for the desktop table view. Rebuilt whenever the filtered
  // list or the sort changes.
  readonly tableRows = computed<Record<string, any>[]>(() => {
    const rows = this.inquiries().map((i) => this.toTableRow(i));
    const sort = this.sortState();
    if (sort?.direction) {
      const direction = sort.direction === 'asc' ? 1 : -1;
      rows.sort((a, b) => {
        const left = a[sort.active]?.sortValue ?? a[sort.active]?.value ?? '';
        const right = b[sort.active]?.sortValue ?? b[sort.active]?.value ?? '';
        if (left < right) {
          return -1 * direction;
        }
        if (left > right) {
          return 1 * direction;
        }
        return 0;
      });
    }
    return rows;
  });

  ngOnInit(): void {
    this.searchForm
      .get('search')!
      .valueChanges.pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.searchTerm.set(value ?? ''));

    timer(60_000, 60_000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshStatusBadges());

    // Acting on an inquiry now happens on its detail page, so the list behind
    // the panel is stale the moment it closes — a declined row would still read
    // "Needs response" until something else triggered a fetch.
    this.rightSidePanelService
      .afterPanelClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.load(true));

    // "Open in Airbnb" opens a new tab, so a host who replies on the channel
    // comes back to a page that never re-fetched — they would see a stale
    // "pending" however fast the backend synced the reply.
    fromEvent(window, 'focus')
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => void this.load(true));

    this.load();
  }

  // Mobile leaves the page entirely rather than opening a panel, so the panel
  // hook above never fires there — re-entering is the equivalent signal.
  ionViewWillEnter(): void {
    if (!this.isLoading()) {
      void this.load(true);
    }
  }

  goBack = (): void => {
    this.navCtrl.navigateForward('more');
  };

  onPropertyFilterChange(value: string | number): void {
    this.selectedPropertyId.set(Number(value));
  }

  // Single-select, like the Bills status filter: picking a status replaces the
  // previous one rather than accumulating.
  onStatusFilterChange(value: string, checked: boolean): void {
    this.selectedStatus.set(checked ? value : null);
  }

  clearStatusFilter(): void {
    this.selectedStatus.set(null);
  }

  sortChanged(sort: Sort): void {
    this.sortState.set(sort);
  }

  // Row click and chevron click are the same event — tidy-table emits cellClick
  // for every cell including 'last-column'.
  onRowClick(event: { column: string; element: any }): void {
    const id = event?.element?.id;
    const row = this.allInquiries().find((i) => i.id === id);
    if (row) {
      void this.openInquiry(row);
    }
  }

  openInquiryCard(inquiry: InquiryVM): void {
    void this.openInquiry(inquiry);
  }

  // Opens the same detail page the Schedule opens for an inquiry card, so both
  // entry points land on one screen. Mirrors ReservationInteractionService's
  // openDetail() rather than calling it, because that takes a schedule
  // Reservation and all this page has is an inquiry id.
  //
  // The page reads `isInquiry` from storage ONLY, so `dialog-params` is written
  // on both branches — on mobile navigateForward's own params never reach it
  // and the modal would render as a reservation.
  private async openInquiry(inquiry: InquiryVM): Promise<void> {
    // Mirrors the params Schedule sends. `address`/`addressId` are unused on
    // the inquiry path, but they are what the reservation path falls back to
    // reading off history state, so sending them keeps the two callers honest.
    // `returnTo` is read by the detail page's post-action close, which would
    // otherwise send a host who arrived from here back to the Schedule.
    const params = {
      guestReservation: { id: inquiry.id },
      isInquiry: true,
      address: inquiry.propertyName,
      addressId: inquiry.addressId,
      returnTo: 'inquiries',
    };
    await this.storage.save('dialog-params', params);
    if (this.windowService.isDesktopRes) {
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.setDialogPageTitle('Inquiry');
      this.rightSidePanelService.openRightSidePanel(ReservationPage);
    } else {
      this.navCtrl.navigateForward('reservation', params);
    }
  }

  // `silent` refreshes in place: no skeleton flash, and a failure stays quiet
  // rather than toasting at a host who only alt-tabbed back to the page.
  private async load(silent = false): Promise<void> {
    if (!silent) {
      this.isLoading.set(true);
    }
    // Resolve the address lookup before mapping, so the first render already
    // shows nicknames instead of flashing the street address. getAddresses()
    // reads the local cache and only refetches when it's stale.
    await this.loadAddresses();
    this.service
      .getInquiries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (inquiries) => {
          this.allInquiries.set(inquiries.map((i) => this.toViewModel(i)));
          this.isLoading.set(false);
        },
        error: () => {
          if (!silent) {
            this.util.showError('Could not load inquiries');
          }
          this.isLoading.set(false);
        },
      });
  }

  // Cache-first read of the host's addresses, keyed by id for row labelling.
  // A failure is non-fatal: rows just fall back to the payload's address1.
  private async loadAddresses(): Promise<void> {
    try {
      const addresses = await this.client.getAddresses();
      this.addressesById = new Map<number, any>(
        (addresses ?? []).map((address: any) => [address.id, address])
      );
    } catch (_err) {
      this.addressesById = new Map<number, any>();
    }
  }

  // The host's nickname for the property when they've set one, else the
  // street address — Client.buildAddressName is the app-wide rule for that,
  // including the "Unit N" prefix handling for address2.
  private propertyLabel(inquiry: Inquiry): string {
    const address = this.addressesById.get(inquiry.address_id);
    return (address && this.client.buildAddressName(address)) || inquiry.property_name;
  }

  private toViewModel(i: Inquiry): InquiryVM {
    const total = i.pricing ? this.money(i.pricing.total_cents, i.pricing.currency) : '—';
    const propertyName = this.propertyLabel(i);
    const propertyLine = i.property_location
      ? `${propertyName} · ${i.property_location}`
      : propertyName;
    // A row nobody can act on any more badges as Expired — a lapsed
    // pre-approval is never written back at all, and the pending sweep is
    // async, so status alone lags reality.
    const lifecycle: InquiryLifecycle = {
      status: i.status,
      platform: i.platform,
      available_actions: i.available_actions,
      expires_at: i.expires_at ?? null,
      check_in_date: i.check_in_date,
    };
    const presentation = inquiryStatusPresentation(lifecycle);
    return {
      id: i.id,
      addressId: i.address_id,
      propertyName,
      source: i.source,
      sourceName: i.source_name,
      sourceLogo: SOURCE_LOGOS[i.source] ?? null,
      guestName: i.guest.name,
      propertyLine,
      searchText: `${i.guest.name} ${propertyLine} ${i.source_name} ${i.guest.email ?? ''}`.toLowerCase(),
      dateRange: this.dateRangeLabel(i.check_in_date, i.check_out_date),
      checkInIso: i.check_in_date ?? '',
      guestCount: i.total_guests ?? 0,
      totalCents: i.pricing?.total_cents ?? 0,
      guestsLabel: i.total_guests ? `${i.total_guests} guest${i.total_guests === 1 ? '' : 's'}` : null,
      totalLabel: total,
      message: i.message || null,
      status: String(presentation.status),
      statusLabel: presentation.label,
      statusDetail: presentation.detail,
      statusTone: presentation.tone,
      statusOrder: presentation.order,
      lifecycle,
    };
  }

  // One tidy-table row: cells keyed by header name, rendered by the shared
  // table component (values via innerHTML). `id` rides along unrendered — it is
  // not in displayedColumns — so cellClick can resolve the row back to an
  // inquiry without a lookup.
  private toTableRow(i: InquiryVM): Record<string, any> {
    return {
      id: i.id,
      'Guest': { value: this.escapeHtml(i.guestName), initials: true },
      'Property': { value: this.escapeHtml(i.propertyLine) },
      'Source': { value: this.sourceCellHtml(i), sortValue: i.sourceName },
      'Dates': { value: i.dateRange, sortValue: i.checkInIso },
      'Guests': { value: i.guestsLabel ?? '—', sortValue: i.guestCount },
      'Special Request': { value: i.message ? this.escapeHtml(i.message) : '—', truncate: true },
      'Total': { value: i.totalLabel, sortValue: i.totalCents },
      'Status': {
        value: this.statusCellHtml(i),
        // Rank, not label: sorting the text would file "Booked" above "Needs
        // response" and bury the rows the host still has to deal with.
        sortValue: i.statusOrder,
      },
      'last-column': { value: '' },
    };
  }

  // Brand-coloured logo disc + channel name, the same pairing Listings shows
  // for a connected listing. Built as markup because tidy-table renders cell
  // values through innerHTML; the card list binds the same classes.
  private sourceCellHtml(i: InquiryVM): string {
    const logo = i.sourceLogo
      ? `<span class="inq-source__avatar inq-source__avatar--${i.source}">` +
        `<img src="${i.sourceLogo}" alt="${this.escapeHtml(i.sourceName)}" /></span>`
      : '';
    return `<span class="inq-source">${logo}${this.escapeHtml(i.sourceName)}</span>`;
  }

  // Coloured dot + plain label, matching how Listings renders a status. The
  // table renders cell values as HTML, so this is built as markup rather than
  // bound — the card list binds the same classes in the template.
  private statusCellHtml(i: InquiryVM): string {
    const detail = i.statusDetail
      ? `<span class="inq-status__detail">${this.escapeHtml(i.statusDetail)}</span>`
      : '';
    return (
      `<span class="inq-status">` +
      `<span class="inq-status__label">` +
      `<span class="inq-status__dot inq-status__dot--${i.statusTone}"></span>` +
      `${this.escapeHtml(i.statusLabel)}</span>${detail}</span>`
    );
  }

  // Cell values render via innerHTML in tidy-table, so markup-significant chars
  // must be escaped. Quotes stay literal: no cell interpolates into an
  // attribute, and leaving them keeps the plain-text truncate tooltip reading
  // naturally.
  private escapeHtml(value: string): string {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // "Mar 3 – 7" when the stay stays in one month, "Mar 30 – Apr 2" when it
  // doesn't, and an honest placeholder when the channel captured no dates.
  private dateRangeLabel(checkIn?: string, checkOut?: string): string {
    if (!checkIn || !checkOut) {
      return 'Dates not shared';
    }
    const start = LuxonDateTime.fromISO(checkIn);
    const end = LuxonDateTime.fromISO(checkOut);
    if (!start.isValid || !end.isValid) {
      return 'Dates not shared';
    }
    const sameMonth = start.hasSame(end, 'month') && start.hasSame(end, 'year');
    return sameMonth
      ? `${start.toFormat('LLL d')} – ${end.toFormat('d')}`
      : `${start.toFormat('LLL d')} – ${end.toFormat('LLL d')}`;
  }

  private money(cents: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  }

  // Every lifecycle badge is time-sensitive, not just its countdown: a
  // pre-approval's 24h window lapses while the page sits open and the backend
  // never rewrites the status when it does, so the move to Expired is entirely
  // ours to make.
  private refreshStatusBadges(): void {
    this.allInquiries.update((list) => {
      const next = list.map((inquiry) => this.withCurrentStatus(inquiry));
      // Every row identical means the minute passed without a badge moving —
      // hand the original array back so the signal stays quiet rather than
      // rebuilding the table underneath the host for nothing.
      return next.some((row, index) => row !== list[index]) ? next : list;
    });
  }

  // Re-derive the badge from the row's lifecycle fields, returning the SAME row
  // object when nothing moved (see refreshStatusBadges). `order` and `status`
  // move with the rest: a row that lapses into Expired must leave the
  // Pre-approved sort slot, and drop out of a "Pre-approved" filter.
  private withCurrentStatus(inquiry: InquiryVM): InquiryVM {
    const { status, label, detail, tone, order } = inquiryStatusPresentation(inquiry.lifecycle);
    const unchanged =
      label === inquiry.statusLabel &&
      detail === inquiry.statusDetail &&
      tone === inquiry.statusTone &&
      order === inquiry.statusOrder;
    return unchanged
      ? inquiry
      : {
          ...inquiry,
          status: String(status),
          statusLabel: label,
          statusDetail: detail,
          statusTone: tone,
          statusOrder: order,
        };
  }
}
